import PropTypes from 'prop-types';
import {useEffect, useState, ReactNode} from "react";
import { Amplify } from "aws-amplify";
import { Hub } from "aws-amplify/utils";
import { fetchAuthSession } from 'aws-amplify/auth';

import EmailPasswordSignIn from './EmailPasswordSignIn';
import FederatedSignIn from "./FederatedSignIn";

import config from '@/aws-exports';
import FullscreenSpinner from '../Spinner/FullscreenSpinner';

Amplify.configure(config);

interface PropTypes {
  children: ReactNode;
  origin?: string;
}

type AuthEvent =
  | "signIn"
  | "cognitoHostedUI"
  | "signOut"
  | "signIn_failure"
  | "cognitoHostedUI_failure";

function App({ children, origin }: Readonly<PropTypes>) {
  const [token, setToken] = useState<string | null | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  useEffect(() => {
    (async function() {
      try {
        const session = await fetchAuthSession();
        const sessionToken = session?.tokens?.idToken?.toString();
        setIsLoading(false);
        setToken(sessionToken);
      } catch (err) {
        console.error("Error fetching auth session", err);
      }
    })();
  }, []);

  useEffect(() => {
    const hubListener = Hub.listen("auth", ({payload: {event}} : {payload: {event: AuthEvent}}) => {
      switch (event) {
        case "signIn":
        // For a reason the signedIn is not part of the types
        // but it's getting returned by the Hub
        // @ts-expect-error signedIn exists in type
        case "signedIn":
        case "cognitoHostedUI":
          setToken("grating...");
          // @ts-expect-error idtoken exists in type
          getToken().then(userToken => setToken(userToken?.idToken?.jwtToken));
          setIsLoading(false);

          break;
        case "signOut":
          setToken(null);
          setIsLoading(false);
          break;
        case "signIn_failure":
        case "cognitoHostedUI_failure":
          setIsLoading(false);
          break;
        default:
          break;
      }
    });

    return () => {
      // Call the unsubscribe function to stop listening to auth events 
      hubListener();
    }
  }, []);

  async function getToken() {
    try {
      const session = await fetchAuthSession();
      return session;
    } catch (err) {
      console.error('Error fetching the session', err);
    }
  }

  if (isLoading) {
    return <FullscreenSpinner />
  }

  const enableEmailLogin = process.env.NEXT_PUBLIC_ENABLE_COGNITO_EMAIL_LOGIN === 'true';

  return (
    <div className={` col-span-full w-full h-screen flex justify-center items-center`}>
      {token ? (
        children
      ) : (
      <div className="flex flex-col items-center md:flex-row gap-8 rounded-lg justify-center h-[500px] p-20 bg-gray-50">
        {enableEmailLogin && <EmailPasswordSignIn />}
        <FederatedSignIn federatedIdName="Okta" origin={origin} />
      </div>
      )}
    </div>
  );
}

App.propTypes = {
  children: PropTypes.any,
};

export default App;